import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { ActionConfirmation, Box, Button } from '@qwealth/qcore';
import { selectPartners, selectUser } from '@qwealth/qdata';

import { selectClientComplianceReview, selectComplianceSelectedChecklist, selectComplianceSelectedChecklistNotes } from 'data/selectors/compliance';
import { useAppDispatch, useAppSelector } from 'data/store';
import {
  ChecklistSubmission,
  ClientComplianceReview,
  COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE,
  ComplianceNotesMap,
  ComplianceQuestion,
} from 'data/models/Compliance';
import { complianceApproval, submitComplianceChecklist } from 'data/actions/compliance';
import { getComplianceNotificationPayload, sendEmailNotification } from 'utils/ComplianceUtil';
import ChecklistSection from './ChecklistSection';
import { complianceRoute } from 'data/refData/compliance';

const Checklist: FC = (): JSX.Element => {
  const user = useAppSelector(selectUser);
  const partnerList = useAppSelector(selectPartners);
  const selectedChecklist = useAppSelector(selectComplianceSelectedChecklist);
  const selectedClient: ClientComplianceReview | undefined = useAppSelector(selectClientComplianceReview);
  const selectedChecklistNotes = useAppSelector(selectComplianceSelectedChecklistNotes) as ComplianceNotesMap;
  const [complianceChecklistAnswers, setComplianceChecklistAnswers] = useState<Record<string, string | null | undefined>>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedHouseholdPartnerId, setSelectedHouseholdPartnerId] = useState<number>();

  useEffect(() => {
    if (selectedChecklist) {
      setComplianceChecklistAnswers(getIdToAnswerMap(selectedChecklist));
    }
  }, [selectedChecklist]);

  useEffect(() => {
    if (!selectedClient) return;
    const partnerName = selectedClient.partnerName;
    const partner = partnerList.find(partner => partner.partnerName === partnerName);
    if (partner?.id) {
      setSelectedHouseholdPartnerId(partner.id);
    }
  }, [selectedClient, partnerList]);

  const getIdToAnswerMap = (checklist: ComplianceQuestion[]) => {
    const result: Record<string, string | null | undefined> = {};
    if (!checklist) {
      return {};
    }
    for (const item of checklist) {
      if (!item.children || item.children.length === 0) {
        // All questions are preset to yes when the checklist is not started
        result[item.id.toString()] = item.value ? item.value : 'yes';
      } else {
        // If the node has children, recursively collect values from them
        const childValues = getIdToAnswerMap(item.children);
        Object.assign(result, childValues); // Merge all the values into a single map
      }
    }

    return result;
  };

  const updateQuestionAnswer = (questionId: string, option: string) => {
    if (complianceChecklistAnswers === undefined) {
      return;
    }
    const updatedSubmission = { ...complianceChecklistAnswers };
    updatedSubmission[questionId] = option;
    setComplianceChecklistAnswers(updatedSubmission);
  };

  const isChecklistNotCompliant = (): boolean => {
    if (!complianceChecklistAnswers) {
      return true;
    }
    return Object.values(complianceChecklistAnswers).some(value => value === 'no');
  };

  const submitChecklist = async (): Promise<boolean | undefined> => {
    const payload = {
      values: complianceChecklistAnswers,
      notes: Object.values(selectedChecklistNotes),
      assignee: user.email ?? null,
    };
    try {
      if (selectedClient?.QID === undefined || payload.values === undefined) {
        throw new Error('Unable to submit checklist, QID or answers is undefined');
      }
      const isChecklistSubmitted = await dispatch(submitComplianceChecklist(selectedClient?.QID, payload as ChecklistSubmission));
      return isChecklistSubmitted;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const sendSubmissionNotification = async () => {
    const shouldSendNotification = isChecklistNotCompliant() && selectedClient && selectedHouseholdPartnerId !== undefined;

    if (shouldSendNotification) {
      const notificationPayload = getComplianceNotificationPayload(
        selectedClient.rtqLevel === COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE,
        selectedClient.QID,
        selectedClient.household.QID ?? '',
        user.email,
        selectedHouseholdPartnerId,
        'ComplianceChecklistActionRequired',
      );

      return await dispatch(sendEmailNotification(notificationPayload));
    }
    return true;
  };

  const clickSubmit = async () => {
    const checklistAnswersDidSubmit = await submitChecklist();
    if (!checklistAnswersDidSubmit) {
      return;
    }

    const notificationsDidSend = await sendSubmissionNotification();
    if (!notificationsDidSend) {
      return;
    }
    navigate(`/${complianceRoute.path}`);
  };

  const complianceApprovalHandler = async () => {
    if (selectedClient?.kycId) {
      await dispatch(complianceApproval(selectedClient.kycId));
      navigate(`/${complianceRoute.path}`);
    }
  };

  return (
    <Box display="flex" flexDirection="column" padding="large" gap="large" maxHeight="100vh">
      {selectedChecklist &&
        selectedChecklist.map(section => {
          return <ChecklistSection key={'ChecklistSection' + section.text} section={section} updateQuestionAnswer={updateQuestionAnswer} />;
        })}

      {isChecklistNotCompliant() ? (
        <Button onClick={() => clickSubmit()} minHeight="50px" fontSize="large">
          Submit Review
        </Button>
      ) : (
        <ActionConfirmation
          actionTitle="Compliance Approval"
          actionLabel="Approve"
          actionHandler={() => {
            complianceApprovalHandler();
          }}
          closeHandler={() => {
            navigate(`/${complianceRoute.path}`);
          }}
          bodyText={
            <Box display="flex" flexDirection="column" gap="default">
              <Box fontWeight="bold" fontSize="large">
                All the items on the checklist have been submitted as compliant.
              </Box>
              <Box fontWeight="bold" fontSize="large">
                Would you like to approve "{selectedClient?.name}"?
              </Box>
            </Box>
          }
          ActionButtonRenderer={openHandler => (
            <Button
              minHeight="50px"
              fontSize="large"
              onClick={() => {
                (async () => {
                  const isChecklistSubmitted = await submitChecklist();
                  if (!isChecklistSubmitted) {
                    return;
                  }
                  openHandler();
                })();
              }}
            >
              Submit Review
            </Button>
          )}
        />
      )}

      <Box minHeight="500px" />
    </Box>
  );
};

export default Checklist;
